import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { TextArea } from "@package/subframe/components/TextArea";
import { TopBar } from "@package/subframe/components/TopBar";
import {
  FeatherAlertCircle,
  FeatherArrowLeft,
  FeatherCircleFadingPlus,
  FeatherClock,
  FeatherRefreshCw,
  FeatherWifiOff,
  FeatherX,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { Camera, ImageIcon } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { OfflineImage } from "@/components/ui/offline-image";
import { UserMenu, UserMenuTrigger } from "@/components/user-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useDeleteFileControllerDeleteFile } from "@/generated/files/files";
import { useUpdateActionStatusCommandControllerUpdateStatus } from "@/generated/follow-up-actions/follow-up-actions";
import type { FollowUpActionResponseDto } from "@/generated/model";
import {
  FollowUpActionResponseDtoSourceType,
  FollowUpActionResponseDtoStatus,
  UpdateActionStatusDtoStatus,
} from "@/generated/model";
import { useOfflineFollowUpActionCompletion } from "@/hooks/use-offline-follow-up-action-completion";
import { trackSessionEnded } from "@/lib/analytics";
import { signOut } from "@/lib/auth";
import {
  formatActionDueDate,
  isActionOverdue,
} from "@/lib/follow-up-action-utils";
import { invalidateFollowUpActionQueries } from "@/lib/follow-up-actions/invalidate-follow-up-action-queries";
import { clearAllOfflineData } from "@/lib/offline/db";
import { canSignOffFollowUp } from "@/lib/roles";

const MAX_PHOTOS = 5;
const MAX_NOTES_LENGTH = 5000;

/**
 * Label for the post-completion "back to where you came from" button.
 *
 * The action's `sourceType` tells us which surface the practitioner was on
 * when they navigated here, so the modal can name it accurately instead of
 * defaulting to "Back to checklists" (which is misleading when the action
 * came from an incident or a compliance task).
 *
 * Note: actual navigation is handled by `onClose`, which uses
 * `window.history.back()` first — so this is purely the label.
 */
function backLabelForSource(
  sourceType: FollowUpActionResponseDto["sourceType"],
): string {
  switch (sourceType) {
    case FollowUpActionResponseDtoSourceType.OUT_OF_RANGE_INCIDENT:
      return "Back to incident";
    case FollowUpActionResponseDtoSourceType.COMPLIANCE_TASK_INSTANCE:
      return "Back to compliance tasks";
    case FollowUpActionResponseDtoSourceType.LOG_ENTRY:
      return "Back to logs";
    case FollowUpActionResponseDtoSourceType.FIRE_EXTINGUISHER_CHECK_ITEM:
      return "Back to fire extinguisher check";
    case FollowUpActionResponseDtoSourceType.EMERGENCY_LIGHT_CHECK_ITEM:
      return "Back to emergency lighting check";
    case FollowUpActionResponseDtoSourceType.EVENT_RECORD:
      return "Back to record";
    case FollowUpActionResponseDtoSourceType.QUESTIONNAIRE_ANSWER:
      return "Back to checklists";
  }
}

interface FollowUpActionCompletionProps {
  action: FollowUpActionResponseDto;
  onClose: () => void;
}

export function FollowUpActionCompletion({
  action,
  onClose,
}: FollowUpActionCompletionProps) {
  const queryClient = useQueryClient();
  const { user, userRoleType } = useAuth();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showLogoutPrompt, setShowLogoutPrompt] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const isWaitingApproval =
    action.status === FollowUpActionResponseDtoStatus.WAITING_APPROVAL;

  const canSignOff = canSignOffFollowUp({ userRoleType });

  const isReadOnly =
    action.status === FollowUpActionResponseDtoStatus.COMPLETED ||
    action.status === FollowUpActionResponseDtoStatus.CANCELLED ||
    isWaitingApproval;

  const {
    notes,
    setNotes,
    photos,
    syncStatus,
    isLocallyCompleted,
    savePhoto,
    deletePhoto,
    completeAction,
    retryFailed,
  } = useOfflineFollowUpActionCompletion(
    action.id,
    action.files,
    action.completionNotes,
  );

  const deleteFile = useDeleteFileControllerDeleteFile();
  const updateStatus = useUpdateActionStatusCommandControllerUpdateStatus();

  const overdue = isActionOverdue(action);
  const dueDateText = formatActionDueDate(action);

  // Treat locally completed same as server completed (matches checklist pattern)
  const isCompleted = isReadOnly || isLocallyCompleted;

  const canAddPhotos = !isCompleted && photos.length < MAX_PHOTOS;

  const userName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(" ") ||
      user.email ||
      "Unknown"
    : "Unknown";

  const userData = {
    name: userName,
    email: user?.email || "",
    avatar: "",
  };

  const handleTakePhoto = useCallback(() => {
    if (!canAddPhotos) return;
    cameraInputRef.current?.click();
  }, [canAddPhotos]);

  const handleChooseFromGallery = useCallback(() => {
    if (!canAddPhotos) return;
    fileInputRef.current?.click();
  }, [canAddPhotos]);

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const remainingSlots = MAX_PHOTOS - photos.length;
      if (remainingSlots <= 0) {
        toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
        e.target.value = "";
        return;
      }

      const filesToAdd = Array.from(files).slice(0, remainingSlots);

      for (const file of filesToAdd) {
        try {
          await savePhoto(file);
        } catch (error) {
          console.error("Failed to save photo:", error);
          toast.error("Failed to save photo");
        }
      }

      toast.success(
        filesToAdd.length === 1
          ? "Photo added"
          : `${filesToAdd.length} photos added`,
      );

      e.target.value = "";
    },
    [photos.length, savePhoto],
  );

  const handleDeletePhoto = useCallback(
    async (photoId: string, isServerFile: boolean) => {
      if (isServerFile) {
        // Server-side file - delete via API (only works online)
        try {
          await deleteFile.mutateAsync({ fileId: photoId });
          toast.success("Photo removed");
        } catch {
          toast.error("Failed to remove photo");
        }
      } else {
        // Local offline photo - remove from IndexedDB
        await deletePhoto(photoId);
        toast.success("Photo removed");
      }
    },
    [deleteFile, deletePhoto],
  );

  const handleComplete = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await completeAction();
      setShowConfirmDialog(false);

      if (navigator.onLine) {
        toast.success("Follow-up action marked as complete");
      } else {
        toast.success("Saved offline - will sync when back online");
      }
      setShowLogoutPrompt(true);
    } catch {
      toast.error("Failed to complete follow-up action");
    } finally {
      setIsSubmitting(false);
    }
  }, [completeAction]);

  const handleSignOut = useCallback(async () => {
    const sessionStartTime = sessionStorage.getItem("session_start_time");
    const sessionDurationSeconds = sessionStartTime
      ? Math.floor((Date.now() - parseInt(sessionStartTime, 10)) / 1000)
      : 0;
    trackSessionEnded("manual", sessionDurationSeconds);
    sessionStorage.removeItem("session_start_time");
    await clearAllOfflineData();
    await signOut();
    window.location.href = "/login";
  }, []);

  const invalidateAndClose = useCallback(async () => {
    await invalidateFollowUpActionQueries(queryClient);
    onClose();
  }, [queryClient, onClose]);

  const handleSignOff = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await updateStatus.mutateAsync({
        actionId: action.id,
        data: { status: UpdateActionStatusDtoStatus.COMPLETED },
      });
      toast.success("Action signed off and completed");
      await invalidateAndClose();
    } catch {
      toast.error("Failed to sign off action");
    } finally {
      setIsSubmitting(false);
    }
  }, [action.id, updateStatus, invalidateAndClose]);

  const handleReopen = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await updateStatus.mutateAsync({
        actionId: action.id,
        data: { status: UpdateActionStatusDtoStatus.OPEN },
      });
      toast.success("Action returned to open");
      await invalidateAndClose();
    } catch {
      toast.error("Failed to reopen action");
    } finally {
      setIsSubmitting(false);
    }
  }, [action.id, updateStatus, invalidateAndClose]);

  return (
    <div className="flex w-full h-full flex-col bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-col items-start shrink-0 bg-white z-10">
        <TopBar
          menuIcon={<FeatherArrowLeft />}
          onMenuClick={onClose}
          header="Follow-up action"
          rightFirst={<UserMenuTrigger user={userData} />}
          userMenu={<UserMenu user={userData} />}
        />
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex w-full max-w-[1024px] m-auto flex-col items-start gap-8 px-4 py-6">
          {/* Action info */}
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {action.description}
            </span>

            {dueDateText && (
              <div className="flex w-full flex-col items-center pb-3 mobile:pb-3">
                <div className="flex w-full items-center gap-1 mobile:gap-2">
                  <FeatherClock
                    className={`text-caption font-caption ${overdue ? "text-error-700" : "text-subtext-color"}`}
                  />
                  <span
                    className={`text-caption font-caption ${overdue ? "text-error-700" : "text-subtext-color"}`}
                  >
                    Due: {dueDateText}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Requires approval note (shown when action is still open/overdue) */}
          {action.requiresApproval && !isWaitingApproval && !isCompleted && (
            <div className="flex w-full items-start gap-3 rounded-md bg-neutral-50 border border-neutral-200 px-4 py-3">
              <FeatherAlertCircle className="text-neutral-500 flex-none mt-0.5" />
              <span className="text-caption font-caption text-subtext-color">
                Once completed, this action will require manager sign-off before
                it is finalised.
              </span>
            </div>
          )}

          {/* Waiting approval banner */}
          {isWaitingApproval && (
            <div className="flex w-full items-start gap-3 rounded-md bg-amber-50 border border-amber-200 px-4 py-3">
              <FeatherClock className="text-amber-600 flex-none mt-0.5" />
              <div className="flex flex-col items-start gap-1">
                <span className="text-body-bold font-body-bold text-amber-800">
                  Awaiting manager sign-off
                </span>
                <span className="text-caption font-caption text-amber-700">
                  {canSignOff
                    ? "Review the completion details below and sign off or return to open."
                    : "This action has been completed and is waiting for a manager to review and sign off."}
                </span>
              </div>
            </div>
          )}

          {/* Completion notes */}
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-neutral-900">
              Notes
              {!isCompleted && (
                <span className="text-caption font-caption text-subtext-color ml-1">
                  (optional)
                </span>
              )}
            </span>
            {isCompleted ? (
              <div className="w-full rounded-md bg-neutral-50 p-3">
                <span className="text-body font-body text-neutral-700">
                  {notes || action.completionNotes || "No notes added"}
                </span>
              </div>
            ) : (
              <TextArea className="h-auto w-full flex-none" variant="outline">
                <TextArea.Input
                  placeholder="Add notes about how this action was completed..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={MAX_NOTES_LENGTH}
                  className="min-h-[100px]"
                />
              </TextArea>
            )}
          </div>

          {/* Evidence photos */}
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-neutral-900">
              Evidence
              {!isCompleted && (
                <span className="text-caption font-caption text-subtext-color ml-1">
                  (optional)
                </span>
              )}
            </span>

            {/* All photos in a single grid */}
            {photos.length > 0 && (
              <div className="w-full items-start gap-3 grid grid-cols-4 mobile:grid mobile:grid-cols-2">
                {photos.map((photo, index) => {
                  const isServerFile = !!photo.downloadUrl;
                  return (
                    <div
                      key={photo.id}
                      className="relative aspect-square rounded-md overflow-hidden bg-muted"
                    >
                      <OfflineImage
                        src={photo.downloadUrl || photo.blobUrl || ""}
                        alt={`Evidence ${index + 1}`}
                        className="w-full h-full object-cover"
                        uploadStatus={
                          photo.uploadStatus as
                            | "PENDING"
                            | "UPLOADING"
                            | "COMPLETED"
                            | "FAILED"
                            | undefined
                        }
                      />
                      {!isCompleted && (
                        <button
                          className="absolute top-1.5 right-1.5 w-6 h-6 bg-neutral-600 hover:bg-neutral-700 rounded-full flex items-center justify-center"
                          onClick={() =>
                            handleDeletePhoto(photo.id, isServerFile)
                          }
                        >
                          <FeatherX className="h-3.5 w-3.5 text-white" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Photo action buttons */}
            {!isCompleted && (
              <div className="flex w-full gap-2">
                <Button
                  className="h-12 grow shrink-0 basis-0"
                  variant="neutral-secondary"
                  size="large"
                  icon={<Camera className="h-4 w-4" />}
                  onClick={handleTakePhoto}
                  disabled={!canAddPhotos}
                >
                  Take photo
                </Button>
                <Button
                  className="h-12 grow shrink-0 basis-0"
                  variant="neutral-secondary"
                  size="large"
                  icon={<ImageIcon className="h-4 w-4" />}
                  onClick={handleChooseFromGallery}
                  disabled={!canAddPhotos}
                >
                  Gallery
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex w-full items-center justify-center gap-4 border-t border-solid border-neutral-200 bg-white px-4 py-3 shrink-0">
        {/* Sync status indicators */}
        {syncStatus.errorCount > 0 && (
          <button
            className="flex items-center gap-2 text-error-700"
            onClick={retryFailed}
          >
            <FeatherAlertCircle className="text-body font-body" />
            <span className="text-body font-body">
              Sync failed ({syncStatus.errorCount})
            </span>
          </button>
        )}
        {syncStatus.isOnline &&
          syncStatus.pendingCount > 0 &&
          syncStatus.errorCount === 0 && (
            <div className="flex items-center gap-2 text-subtext-color">
              <FeatherRefreshCw className="text-body font-body animate-spin" />
              <span className="text-body font-body">
                Syncing ({syncStatus.pendingCount})
              </span>
            </div>
          )}
        {!syncStatus.isOnline && syncStatus.errorCount === 0 && (
          <div className="flex items-center gap-2">
            <FeatherWifiOff className="text-body font-body text-subtext-color" />
            <span className="text-body font-body text-subtext-color">
              Offline
            </span>
          </div>
        )}

        {/* Manager sign-off/reopen buttons for WAITING_APPROVAL */}
        {isWaitingApproval && canSignOff ? (
          <div className="flex grow shrink-0 basis-0 gap-2">
            <Button
              className="h-12 grow shrink-0 basis-0"
              variant="neutral-secondary"
              size="extra-large"
              onClick={handleReopen}
              disabled={isSubmitting}
            >
              Return to open
            </Button>
            <Button
              className="h-12 grow shrink-0 basis-0"
              variant="brand-primary"
              size="extra-large"
              onClick={handleSignOff}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Signing off..." : "Sign off"}
            </Button>
          </div>
        ) : (
          <Button
            className="h-12 grow shrink-0 basis-0"
            variant="brand-primary"
            size="extra-large"
            icon={null}
            onClick={isCompleted ? undefined : () => setShowConfirmDialog(true)}
            disabled={isCompleted || isSubmitting}
          >
            {action.status === FollowUpActionResponseDtoStatus.COMPLETED ||
            isLocallyCompleted
              ? "Completed"
              : isWaitingApproval
                ? "Awaiting sign-off"
                : action.status === FollowUpActionResponseDtoStatus.CANCELLED
                  ? "Cancelled"
                  : isSubmitting
                    ? "Completing..."
                    : "Complete"}
          </Button>
        )}
      </div>

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileSelected}
      />
      <input
        type="file"
        ref={cameraInputRef}
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Logout prompt dialog */}
      <Dialog open={showLogoutPrompt} onOpenChange={setShowLogoutPrompt}>
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white mobile:flex-col mobile:flex-nowrap mobile:gap-6 mobile:px-4 mobile:py-4">
            <div className="flex max-w-[448px] flex-col items-center gap-6 p-4 mobile:w-full mobile:grow mobile:shrink-0 mobile:basis-0 mobile:items-center mobile:justify-center">
              <div className="flex flex-col items-center gap-2">
                <span className="w-full text-heading-2 font-heading-2 text-default-font text-center mobile:text-primary-navy-800">
                  Action complete
                </span>
                <span className="text-body font-body text-subtext-color text-center mobile:text-center mobile:px-4">
                  Would you like to log out?
                </span>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4 mobile:items-center mobile:justify-center mobile:px-0 mobile:py-0">
              <Button
                className="h-14 w-80 flex-none mobile:h-14 mobile:w-full mobile:flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={handleSignOut}
              >
                Log out
              </Button>
              <Button
                className="h-14 w-80 flex-none mobile:h-14 mobile:w-full mobile:flex-none"
                variant="brand-primary"
                size="extra-large"
                onClick={onClose}
              >
                {backLabelForSource(action.sourceType)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog – same pattern as checklist execution */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white mobile:flex-col mobile:flex-nowrap mobile:gap-6 mobile:px-4 mobile:py-4">
            <div className="flex max-w-[448px] flex-col items-center gap-6 p-4 mobile:w-full mobile:grow mobile:shrink-0 mobile:basis-0 mobile:items-center mobile:justify-center">
              <div className="flex flex-col items-center gap-6 mobile:h-auto mobile:w-full mobile:flex-none">
                <IconWithLogoBackground
                  variant="neutral"
                  size="x-large"
                  icon={<FeatherCircleFadingPlus />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="w-full text-heading-2 font-heading-2 text-default-font text-center mobile:text-primary-navy-800">
                    Mark as complete?
                  </span>
                  <span className="text-body font-body text-subtext-color text-center mobile:text-center mobile:px-4">
                    {action.requiresApproval
                      ? "This will submit the action for manager sign-off."
                      : "This will mark the follow-up action as completed. This cannot be undone."}
                  </span>
                </div>
              </div>
              <div className="grid w-full grid-cols-[auto_1fr] gap-x-4 gap-y-3 rounded-lg bg-neutral-50 px-4 py-4 mobile:h-auto mobile:w-full mobile:flex-none">
                <span className="text-body font-body text-subtext-color">
                  Action
                </span>
                <span className="break-all text-right text-body-bold font-body-bold text-neutral-900">
                  {action.description}
                </span>
                <span className="text-body font-body text-subtext-color">
                  Completed by
                </span>
                <span className="break-all text-right text-body-bold font-body-bold text-neutral-900">
                  {userName}
                </span>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4 mobile:items-center mobile:justify-center mobile:px-0 mobile:py-0">
              <Button
                className="h-14 w-80 flex-none mobile:h-14 mobile:w-full mobile:flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={() => setShowConfirmDialog(false)}
              >
                Go back
              </Button>
              <Button
                className="h-14 w-80 flex-none mobile:h-14 mobile:w-full mobile:flex-none"
                variant="brand-primary"
                size="extra-large"
                onClick={handleComplete}
              >
                {action.requiresApproval
                  ? "Yes, submit for sign-off"
                  : "Yes, mark as complete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
